import { addLog } from 'assets/components/feedback/Feedback';
import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useDataProvider } from 'assets/providers/data/DataProvider';
import { date, differenceInMonthsOnly } from 'assets/utils/data/Date';
import { autoTriggerDownload } from 'assets/utils/dom/Download';
import { generateDataUrl } from 'assets/utils/dom/UrlParsing';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import dayjs from 'dayjs';
import { entries, first, groupBy, isEmpty, orderBy } from 'lodash';
import providerStatementBalanceEmailTemplateListApi from 'models/core/providerStatementBalanceEmailTemplate/list/Api.ProviderStatementBalanceEmailTemplate.List';
import StatementBalanceEmailTemplate from 'models/core/statementBalanceEmailTemplate/Model.StatementBalanceEmailTemplate';
import Dealer from 'models/dealerManagement/dealer/Model.Dealer';
import DealerGroup from 'models/dealerManagement/dealerGroup/Model.DealerGroup';
import { entityTranslationListApi } from 'models/localization/entityTranslation/Model.EntityTranslation';
import Contract from 'models/sales/contract/Model.Contract';
import StatementBalance from 'models/sales/statementsBalance/Model.StatementBalance';
import statementBalanceCreateForDealerApi from 'models/sales/statementsBalance/createForDealer/Api.StatementBalance.CreateForDealer';
import statementBalanceDeleteApi from 'models/sales/statementsBalance/delete/Api.StatementBalance.Delete';
import statementBalanceGeneratePdfApi from 'models/sales/statementsBalance/generatePdf/Api.StatementBalance.GeneratePdf';
import statementBalanceListApi from 'models/sales/statementsBalance/list/Api.StatementBalance.List';
import statementBalanceSendEmailApi from 'models/sales/statementsBalance/sendEmail/Api.StatementBalance.SendEmail';
import { StatementBalanceEmailTriggers } from 'module/management/views/statementBalanceEmailTemplate/StatementBalanceEmailTemplate.Hooks';
import { useMemo, useState } from 'react';
import { statementBalanceIndexRoute } from './StatementBalance.Index';

export enum StatementBalanceAction {
  CREATE = 'create',
  UPLOAD_DOCUMENT = 'uploadDocument',
  PRINT = 'print',
  SEND_EMAIL = 'sendEmail',
  DELETE = 'delete',
  GENERATE_PDF = 'generatePdf',
}
export type VerifyStatementBalanceAction = Utils.VerifyExtends<
  Module.Claims.StatementBalance.Actions,
  Utils.ValueOf<typeof StatementBalanceAction>
>;

export default function useStatementBalance() {
  const { lang } = getLocales();
  const { user, permissions } = useAuthStore();
  const pageRouter = usePageRouter<Module.Claims.StatementBalance.Params, Module.Claims.StatementBalance.Query>({
    route: statementBalanceIndexRoute,
  });
  const [selectedBalanceStatements, setSelectedBalanceStatements] = useState<Model.IStatementBalance[]>([]);

  const canView =
    permissions.CREATE_STATEMENTBALANCE ||
    permissions.EDIT_STATEMENTBALANCE ||
    permissions.DELETE_STATEMENTBALANCE ||
    permissions.MODIFY_STATEMENTBALANCE_EMAIL;

  const timeRangesLabels = ['zeroTo30Days', 'thirtyOneTo60Days', 'sixtyOneTo90Days', 'ninetyOnePlusDays'];

  //#region APIS
  const listApi = useApi({
    action: StatementBalance.list,
    default: {
      limit: defaultLimit,
      offset: defaultOffset,
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      dealerId: user?.dealers?.map((it) => it.id),
    },
    body: {
      ...pageRouter.query.list,
    },
    callback: (req) => pageRouter.redirect({}, { ...pageRouter.query, list: req }),
    wait: !canView,
  });

  const deleteApi = useApi({
    action: statementBalanceDeleteApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const createApi = useApi({
    action: statementBalanceCreateForDealerApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const dealersApi = useApi({
    action: Dealer.list,
    body: {
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      id: user?.dealers?.map((it) => it.id) ?? [],
    },
  });

  const dealerGroupsApi = useApi({
    action: DealerGroup.list,
    body: {
      distributorId: user?.distributorId ? [user.distributorId] : undefined,
      dealerId: user?.dealers?.map((it) => it.id) ?? [],
    },
  });

  const selected = useMemo(() => {
    return listApi.payload?.data?.find((it) => it.id.toString() === pageRouter.params.id?.toString());
  }, [listApi.payload?.data, pageRouter.params.id]);

  const sendEmailApi = useApi({
    action: statementBalanceSendEmailApi,
    callback: () => listApi.execute((body) => body),
    wait: true,
  });

  const statementBalanceEmailTemplatesApi = useApi({
    action: StatementBalanceEmailTemplate.list,
    body: { statementBalanceStatus: [StatementBalanceEmailTriggers.SendEmail] },
  });
  const statementBalanceEmailTemplate = first(statementBalanceEmailTemplatesApi.payload?.data);
  async function getEmailTemplate(providerId: number, languageIsoCode: string) {
    if (!statementBalanceEmailTemplate) return undefined;

    let template: Utils.Email.IEmailTemplate & {
      attachments?: Utils.Email.IAttachment[];
    } = {
      ...statementBalanceEmailTemplate,
    };

    const resProviderStatementEmailTemplate = await providerStatementBalanceEmailTemplateListApi({
      statementBalanceStatus: [StatementBalanceEmailTriggers.SendEmail],
      statementBalanceEmailTemplateId: statementBalanceEmailTemplate?.id
        ? [statementBalanceEmailTemplate?.id]
        : undefined,
      providerId: providerId ? [providerId] : undefined,
    });

    const providerEmailTemplate = resProviderStatementEmailTemplate.payload?.data?.find(
      (it) => it.statementBalanceEmailTemplateId === statementBalanceEmailTemplate?.id && it.providerId === providerId
    );

    if (providerEmailTemplate) {
      template = { ...providerEmailTemplate };

      const translationsApi = await entityTranslationListApi({
        entityType: 'ProviderStatementBalanceEmailTemplate',
        entityId: providerEmailTemplate?.id ? [providerEmailTemplate?.id] : undefined,
      });

      if (!isEmpty(translationsApi?.payload?.data)) {
        const translation = translationsApi?.payload?.data.find(
          (it) => it.entityId === providerEmailTemplate?.id && it.entityType === 'ProviderStatementBalanceEmailTemplate'
        );

        if (translation?.languageIsoCode === languageIsoCode) {
          if (translation?.propertyValues['Subject']) {
            template.subject = translation.propertyValues['Subject'];
          }
          if (translation?.propertyValues['HTMLBody']) {
            template.htmlBody = translation.propertyValues['HTMLBody'];
          }
        }
      }
    }
    return template;
  }
  //#endregion
  const [isSendingEmails, setIsSendingEmails] = useState<boolean>(false);
  async function sendEmailsForSelectedBalanceStatements() {
    setIsSendingEmails(true);
    let totalEmailsToSend = 0;
    let totalEmailsSend = 0;
    let totalEmailsFailed = 0;

    if (!isEmpty(selectedBalanceStatements)) {
      for (const statement of selectedBalanceStatements) {
        totalEmailsToSend += 1;

        const emailTemplate = await getEmailTemplate(
          statement.dealer?.providerId,
          statement.dealer?.documentLanguageIsoCode
        );

        if (emailTemplate) {
          const res = await statementBalanceSendEmailApi({
            statementBalanceId: statement.id,
            emailTemplate: {
              ...emailTemplate,
              additionalRecipientEmails: [...(emailTemplate?.additionalRecipientEmails || [])],
            } as Model.IStatementBalanceEmailTemplate,
          });

          if (res.status === HttpStatus.ok) totalEmailsSend += 1;
          else totalEmailsFailed += 1;
        }
      }
    } else addLog({ warning: lang.noStatementBalancesSelectedForSendingAnEmail });

    if (totalEmailsSend > 0) addLog({ success: `Successfully sent: ${totalEmailsSend}/${totalEmailsToSend} emails` });
    else if (totalEmailsFailed > 0)
      addLog({ error: `Failed to send: ${totalEmailsFailed}/${totalEmailsToSend} emails` });

    setIsSendingEmails(false);
    setSelectedBalanceStatements(undefined);
    listApi.execute((b) => b);
  }

  async function exportStatementBalanceToPdf(statementBalanceId: number, statementBalanceNumber: number) {
    const res = await statementBalanceGeneratePdfApi(statementBalanceId);

    if (res?.status === HttpStatus.ok) {
      autoTriggerDownload({
        url: generateDataUrl(MimeType.appPDF, res?.payload),
        name: `${lang.statementBalance} #${statementBalanceNumber}.pdf`,
      });
      addLog({ success: lang.exportSuccess });
    } else addLog({ error: res.message });
  }

  const getAllSelectedBalanceStatements = async () => {
    const newQuery = { ...pageRouter?.query?.list };
    delete newQuery.limit;
    delete newQuery.offset;
    const {
      payload: { data: filteredBalanceStatements },
    } = await statementBalanceListApi({ ...newQuery });

    return selectedBalanceStatements?.length === filteredBalanceStatements?.length
      ? []
      : filteredBalanceStatements?.map((it) => it);
  };

  const getContractsPaymentAmounts = (contracts: Contract[]) => {
    return contracts?.reduce(
      (acc, current) => ({
        paymentAmountCAD: acc.paymentAmountCAD + current.paymentAmountCAD,
        paymentTaxCAD: acc.paymentTaxCAD + current.paymentTaxCAD,
        paymentAmountWithTaxCAD: acc.paymentAmountWithTaxCAD + current.paymentAmountWithTaxCAD,
      }),
      {
        paymentAmountCAD: 0.0,
        paymentTaxCAD: 0.0,
        paymentAmountWithTaxCAD: 0.0,
      }
    );
  };

  function groupContractsByProduct(contracts: Contract[]) {
    return Object.fromEntries(
      entries(groupBy(contracts, (contract) => contract.product.name)).map(([key, contracts]) => [
        key,
        orderBy(contracts, ['createdAtLocal'], ['desc']),
      ])
    );
  }

  function filterContractByMonths(statementDate: dayjs.Dayjs, contractDate: dayjs.Dayjs) {
    const totalDifferenceInMonths = differenceInMonthsOnly(statementDate, contractDate);

    const labelIndex =
      totalDifferenceInMonths > timeRangesLabels.length ? timeRangesLabels.length - 1 : totalDifferenceInMonths - 1;

    return Math.max(labelIndex, 0);
  }

  const statementBalanceContractsPerMonths = entries(
    groupBy(selected?.contracts, (contract) =>
      filterContractByMonths(date(selected?.createdAtLocal), date(contract.createdAtLocal))
    )
  ).map(([idx, contracts]) => ({
    label: timeRangesLabels[idx],
    productContractsDict: groupContractsByProduct(contracts),
    length: contracts?.length,
    paymentAmountCAD: getContractsPaymentAmounts(contracts).paymentAmountCAD.toFixed(2),
    paymentTaxCAD: getContractsPaymentAmounts(contracts).paymentTaxCAD.toFixed(2),
    paymentAmountWithTaxCAD: getContractsPaymentAmounts(contracts).paymentAmountWithTaxCAD.toFixed(2),
  }));

  return {
    canView,
    pageRouter,
    listApi,
    deleteApi,
    createApi,
    selected,
    permissions,
    dealersApi,
    dealerGroupsApi,
    sendEmailApi,
    statementBalanceEmailTemplate,
    selectedBalanceStatements,
    getEmailTemplate,
    statementBalanceContractsPerMonths,
    canModifyEmail: permissions?.MODIFY_STATEMENTBALANCE_EMAIL,
    setSelectedBalanceStatements,
    sendEmailsForSelectedBalanceStatements,
    getAllSelectedBalanceStatements,
    getContractsPaymentAmounts,
    exportStatementBalanceToPdf,
    isSendingEmails,
    setIsSendingEmails,
    views: {
      [StatementBalanceAction.DELETE]:
        pageRouter.params.action === StatementBalanceAction.DELETE && selected && permissions.DELETE_STATEMENTBALANCE,
      [StatementBalanceAction.CREATE]:
        pageRouter.params.action === StatementBalanceAction.CREATE &&
        ((permissions.CREATE_STATEMENTBALANCE && !selected) || (permissions.EDIT_STATEMENTBALANCE && selected)),
      [StatementBalanceAction.UPLOAD_DOCUMENT]:
        pageRouter.params.action === StatementBalanceAction.UPLOAD_DOCUMENT &&
        permissions.EDIT_STATEMENTBALANCE &&
        selected,
      [StatementBalanceAction.PRINT]:
        pageRouter.params.action === StatementBalanceAction.PRINT &&
        (permissions.CREATE_STATEMENTBALANCE || permissions.EDIT_STATEMENTBALANCE) &&
        selected,
      [StatementBalanceAction.GENERATE_PDF]:
        pageRouter.params.action === StatementBalanceAction.GENERATE_PDF &&
        (permissions.CREATE_STATEMENTBALANCE || permissions.EDIT_STATEMENTBALANCE) &&
        selected,
      [StatementBalanceAction.SEND_EMAIL]:
        pageRouter.params.action === StatementBalanceAction.SEND_EMAIL &&
        (permissions.CREATE_STATEMENTBALANCE ||
          permissions.EDIT_STATEMENTBALANCE ||
          permissions.MODIFY_STATEMENTBALANCE_EMAIL) &&
        selected,
    },
  };
}

export function useStatementBalanceProvider() {
  return useDataProvider<ReturnType<typeof useStatementBalance>>();
}
